import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { AuthService } from "../services/auth.service";
import { inject } from "@angular/core";
import { catchError, from, switchMap, tap, throwError } from "rxjs";
import { LoadingIndicatorService } from "../../app/common/loading/loading-indicator.service";
import { NotificationService } from "../../app/common/notification.service";
import { ResultHelper } from "../../app/common/result-helper.service";
import { Result } from "../../app/common/result.model";
import { UserSubscriptionService } from "../../user/services/user-subscription.service";

let isRefreshing = false;
const tooManyRequestsStatusCode = 429;
const tooManyRequestsStatusMessage = "Prea multe solicitări. Vă rugăm să încercați din nou mai târziu.";
const subscriptionExpiredCode = "SUBSCRIPTION_EXPIRED";

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const notificationService = inject(NotificationService);
  const loadingService = inject(LoadingIndicatorService);
  const userSubscriptionService = inject(UserSubscriptionService);
  
  // Always include credentials
  const clonedReq = req.clone({ withCredentials: true });

  let isAuthenticated: boolean;

  authService.isAuthenticated$.subscribe(value => isAuthenticated = value);
  
  return next(clonedReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        handleSuccessResponse(event, notificationService);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle different error statuses
      if (error.status === 0) {
        notificationService.showError('Comunicarea cu server-ul nu s-a putut stabili. Vă rugăm să verificați conexiunea la internet și să încercați din nou.', 5000);
        return throwError(() => error);
      }

      if (error.status === 400 && error.error.error === 'Invalid CSRF token'){
        return from(authService.fetchUserInfo()).pipe(
          switchMap(info => {
            if(info) {
               let headers = new HttpHeaders();
                const requestToken = localStorage.getItem('csrf-request-token');
                if (requestToken) {
                  headers = new HttpHeaders().set('X-XSRF-TOKEN', requestToken);
                }
              const clonedReqWithXsrf = clonedReq.clone({ withCredentials: true, headers: headers });
              return next(clonedReqWithXsrf);
            }
            else {              
              return throwError(() => error);
            }
          }),
          catchError(() => {
            notificationService.showError('A aparut o eroare la validarea utilizatorului. Incercati sa reincarcati pagina.', 5000);
            return throwError(() => error);
          })
        )
      }

      if (error.status === 403 && error.error?.code === subscriptionExpiredCode && !isRefreshing && isAuthenticated === true) {
        isRefreshing = true;

        return from(
          userSubscriptionService.deactivateExpired()
            .then(() => authService.tryRefreshingTokens())
            .then(() => authService.fetchUserInfo())
            .catch(() => { /* swallow — deactivate/refresh/fetchUserInfo failures are non-blocking */ })
            .finally(() => { isRefreshing = false; })
        ).pipe(
          switchMap(() => throwError(() => error))
        );
      }

      if (error.status === 401 && !isProtectedUrl(req.url) && !isRefreshing && isAuthenticated === true) {
        // Try to refresh token for 401 errors
        isRefreshing = true;
        
        return from(authService.tryRefreshingTokens()).pipe(
          switchMap(success => {
            isRefreshing = false;
            
            if (success) {
              // Retry request silently
              return next(clonedReq);
            } else {
              // Show error and redirect              
              authService.cleanUpSession();
              return throwError(() => error);
            }
          }),
          catchError(() => {
            isRefreshing = false;
            notificationService.showError('Sesiunea a expirat. Vă rugăm să vă autentificați din nou.', 5000);
            authService.logout();
            return throwError(() => error);
          })
        );
      }
      
      if(isAuthenticated === true && !isRefreshing){
        handleError(error, notificationService);
      }
      else if(error.status === tooManyRequestsStatusCode){
        notificationService.showError(tooManyRequestsStatusMessage, 5000);
      }

      loadingService.stopAllLoading();

      // Handle other errors      
      return throwError(() => error);
    })
  );
}

function isProtectedUrl(fullUrl: string): boolean {
    const url = new URL(fullUrl, window.location.origin);
    const path = url.pathname;  
    const isAuthRelated =
      path.includes('/auth/login') ||
      path.includes('/auth/register') ||
     // path.includes('/auth/token/refresh') ||
    //   path.includes('/auth/logout') ||
      path.includes('/auth/email-exists') ||
      path.includes('/auth/google-signin') ||
      path.includes('/user/forgot-password') ||
      path.includes('/user/reset-password') ||
      path.includes('/auth/confirm-account') ||
      path.includes('/auth/resend-confirmation-account');

    const isAsset = path.startsWith('/assets/');

    return path.startsWith('/api') && isAuthRelated && isAsset;
}

function handleSuccessResponse(response: HttpResponse<any>, notificationService: NotificationService): void {
  const body = response.body;
  
  // Verificăm dacă răspunsul are structura Result
  if (isResultResponse(body)) {
    if (ResultHelper.isFailure(body)) {
      // Răspunsul HTTP este 200, dar Result.IsSuccess = false
      const error = ResultHelper.getError(body);
      if (error) {
        notificationService.showError(error.message, 5000);
      }
    }
  }
}

function isResultResponse(body: any): body is Result {
  return body && 
         typeof body === 'object' && 
         typeof body.isSuccess === 'boolean' && 
         typeof body.isFailure === 'boolean' &&
         body.error !== undefined;
}

function handleError(error: HttpErrorResponse, notificationService: NotificationService): void {
  const errorMessage = 'A apărut o eroare.';
  
  switch (error.status) {
    case 400:
    case 409:
      notificationService.showError(error.error.detail || errorMessage, 5000);
      break;
    case 401:
       if (error.error.message) {
            notificationService.showError(error.error.message, 5000);
          } else {
            notificationService.showError(errorMessage, 5000);
          }
      break;
    case tooManyRequestsStatusCode:
      notificationService.showError(tooManyRequestsStatusMessage, 5000);
      break;
    default:
      notificationService.showError(errorMessage, 5000);
      break;
  }
}